import DockableWindow from "./dockableWindow";
import DockingMain from "./dockingMain";
import DockableMonitor from "./dockableMonitor";

export type BoundingBox = {
	min: {
		x: number;
		y: number;
	};
	max: {
		x: number;
		y: number;
	};
};

export const EmptyBoundingBox: BoundingBox = { min: { x: 0, y: 0 }, max: { x: 0, y: 0 } };

export type SnappingRegions = {
	left: BoundingBox;
	bottom: BoundingBox;
	right: BoundingBox;
	top: BoundingBox;
	topLeft?: BoundingBox;
	topRight?: BoundingBox;
	rightTop?: BoundingBox;
	rightBottom?: BoundingBox;
	bottomLeft?: BoundingBox;
	bottomRight?: BoundingBox;
	leftTop?: BoundingBox;
	leftBottom?: BoundingBox;
	inner?: BoundingBox;
};

export type MonitorRectangle = {
	left: number;
	top: number;
	right: number;
	bottom: number;
	width?: number;
	height?: number;
};

export type MonitorDimensions = {
	left: number;
	top: number;
	right: number;
	bottom: number;
	width: number;
	height: number;
	name: string;
};
export type CompleteBounds = Required<MonitorRectangle>;
export type IncompleteBounds = Partial<MonitorRectangle>;

export type BoxCoordinates = {
	left: number;
	top: number;
	right: number;
	bottom: number;
	width?: number;
	height?: number;
	windowBoundingBox?: BoundingBox;
	name?: string;
	persistBounds?: boolean;
	finished?: boolean;
};

export type BoxEdge = {
	left?: number;
	top?: number;
	right?: number;
	bottom?: number;
};

export type TetrisBounds = {
	left: number;
	right: number;
	bottom: number;
	top: number;
	width: number;
	height: number;
	vertices: Record<string, Vertex>;
};

export const InitializedBoxCoordinates: BoxCoordinates = { left: 0, right: 0, top: 0, bottom: 0 };

export type XYCoordinates = {
	x: number;
	y: number;
};

export type TopLeftCoordinates = {
	top: number;
	left: number;
};

export type XYOrTopLeft = XYCoordinates | TopLeftCoordinates;

export type Vertex = {
	x: number;
	y: number;
	label: string;
	top?: number;
	left?: number;
};

export type MoveRequest = {
	left: number;
	right: number;
	bottom: number;
	top: number;
	width: number;
	height: number;
	dockedWidth?: number;
	dockedHeight?: number;
	windowBoundingBox: BoundingBox;
	innerBuffer: BoundingBox;
	snappingRegions: SnappingRegions;
	mousePosition: XYCoordinates;
	changeType: number;
	snappedEdge: string;
	// This is set when moving a group
	name?: string;
	dockableEdges?: string[];
};

export type BetweenParams = {
	num: number;
	min: number;
	max: number;
	inclusive: boolean;
};

export type Edges = {
	left: BoundingBox;
	right: BoundingBox;
	bottom: BoundingBox;
	top: BoundingBox;
};

export type SharedEdges = {
	left: boolean;
	right: boolean;
	bottom: boolean;
	top: boolean;
};

// distinction is in the placement. a window placed to the side and top aligned would share the rightTop corner, but not the topRight
export type SharedCorners = {
	// Matched corners
	topLeft: boolean;
	topRight: boolean;
	bottomLeft: boolean;
	bottomRight: boolean;
	// Adjoined corners
	rightTop: boolean;
	rightBottom: boolean;
	leftTop: boolean;
	leftBottom: boolean;
};

export type SnappedObject = {
	canGroup: boolean;
	name: string;
	edges: SharedEdges;
	corners: SharedCorners;
};

export type DockingConfig = {
	name: string;
	isMovable?: boolean;
	ungroupedAlwaysOnTopState?: object;
	MINIMUM_HEIGHT: number;
	MINIMUM_WIDTH: number;
};

export type SystemMove = {
	name: string;
	bounds: BoxCoordinates;
	isMaximized?: boolean;
};

export type JSONGroupRepresentation = {
	windowNames: string[];
	isMovable: boolean;
	isAlwaysOnTop: boolean;
	topRightWindow: string;
	topRightWindows: string[];
	isARectangle: boolean;
	ungroupedAlwaysOnTopState: object;
};

export type SnapObject = {
	name: string;
	edge: string;
	segment?: Edges | BoundingBox[];
	handle?: string;
	win: DockableWindow;
};

export type Point = {
	name: string;
	val: XYCoordinates;
};

export type Corner = {
	window?: string;
	x: number;
	y: number;
	label: string;
};

export type Edge = {
	window?: string;
	min: {
		x: number;
		y: number;
	};
	max: {
		x: number;
		y: number;
	};
};

export type CornersAndEdges = {
	corners: Corner[];
	edges: Edge[];
};

export type DockingBounds = {
	left: number;
	right: number;
	height?: number;
	width?: number;
	bottom?: number;
	top?: number;
	internal?: boolean;
	persistBounds?: boolean;
	duration?: number;
	// is used only for native windows
	notUpdateAssimilation?: boolean;
};

export type MoveToParams = {
	top?: number;
	left?: number;
	height?: number;
	name?: string;
	changeType?: number;
	anchor: DockableWindow;
	systemMoved?: boolean;
	triggeredByAutoArrange: boolean;
};

export type ScaledBounds = {
	left: number;
	top: number;
	width: number;
	height: number;
	right?: number;
	bottom?: number;
	name?: string;
	limits?: number;
};

export type DockableMonitorParams = {
	dockingMain: DockingMain;
	bounds: BoundingBox;
	monitor: DockableMonitor;
};

export type DockableMonitorJSON = {
	bounds: BoundingBox;
	availableRect: DockingBounds;
	monitorRect: any;
	name: string;
	top: number;
	right: number;
	bottom: number;
	left: number;
};

export type DockingConfigOptions = {
	dockable?: boolean;
	isDocked?: boolean;
	dockedWidth?: number;
	dockedHeight?: number;
	height?: number;
	undockedWidth?: number;
	undockedHeight?: number;
	ephemeral?: boolean;
	undockedParams?: DockedDimensions;
	dockedParams?: DockedDimensions;
	startsDocked?: boolean;
	overwriteDockedOptions?: boolean;
	ignoreSnappingRequests?: boolean;

	showTaskbarIcon?: boolean;
	minHeight?: number;
	maxHeight?: number;
	minWidth?: number;
	maxWidth?: number;
	claimSpace?: {
		enabled: boolean;
		alwaysOnTop?: boolean;
	};
	preventOverlapClaimSpace?: boolean;
	BUFFER_SIZE?: number;
	assimilationEnabled?: boolean;
	DEBUG?: boolean;
	ALLOW_GROUPS_TO_SNAP?: boolean;
	GROUP_MODE?:
		| boolean
		| {
				enabled: boolean;
				behavior: string;
				groupTileBuffer: number;
				headerHeight: number;
				requireRectangularityForGroupResize: number;
				fillHolesOnUndock: boolean;
				undockDisbandsEntireGroup: boolean;
		  };
	SNAPPING_OPACITY?: number;
	RESIZE_EVENT_THROTTLE_PERIOD?: number;
	nativeWindowEdgeTolerance?: number;
};

export type DefaultDockingOptions = {
	isDockable: boolean;
	undockedWidth: number;
	undockedHeight: number;
	isDocked: boolean;
	dockedWidth: number;
	dockedHeight: number;
	height: number;
	showTaskbarIcon?: boolean;
	locValue?: string;
};

export const defaultDockingOptions: DefaultDockingOptions = {
	isDockable: false,
	undockedWidth: 300,
	undockedHeight: 30,
	isDocked: false,
	dockedWidth: 300,
	dockedHeight: 39,
	height: 39,
};

export type DockedDimensions = {
	width: number;
	height: number;
	alwaysOnTop?: boolean;
};

export type MonitorDockablePositions = {
	TOP?: number;
	LEFT?: number;
	RIGHT?: number;
	BOTTOM?: number;
	NONE?: number;
};

export type DockingSimulatedEvent = {
	data: any;
	source?: string;
};

export type BoundingBoxesDiag = { A?: any; B?: any; C?: any; D?: any; E?: any; F?: any; G?: any; H?: any; I?: any };

export type ResizeObject = {
	// This used to be derived from the window because it was only calculated on mouseDown. Now that it can be calculated at any time, we need to derive the handle from the request that's coming in. On group resizes, window bounds are only modified on mouse up.
	handle?: string;
	type?: string;
	scalingGroup?: boolean;
	correctedHandle?: string;
	movingEdges?: {
		top: boolean;
		right: boolean;
		left: boolean;
		bottom: boolean;
	};
};
